// src/components/HubCard.jsx
"use client";

import dynamic from "next/dynamic";
import { Share2 } from "lucide-react";
import { useCallback, useMemo, useState } from "react";
import { CardShell, CardText } from "@/components/card-primitives";
import { shareWithSystem } from "@/lib/nativeShare";
import { usePortal } from "@/context/PortalContext";
import { getShareBranding, shareTitle } from "@/lib/portal/shareBranding.mjs";

const ShareModal = dynamic(() => import("@/components/ShareModal"), {
  ssr: false,
  loading: () => null,
});

export default function HubCard({ hub, isOpen, onToggle }) {
  const portal = usePortal();
  const shareBranding = useMemo(() => getShareBranding(portal), [portal]);
  const slug = hub?.slug || "space";
  const name = hub?.name || hub?.displayName || toTitle(slug);
  const desc = hub?.description || "";
  const badge = hub?.badgeUrl;
  const [shareOpen, setShareOpen] = useState(false);
  const [sharePayload, setSharePayload] = useState(null);

  const buildSharePayload = useCallback(() => {
    if (!slug) return null;
    const origin = typeof window !== "undefined" ? window.location.origin : "";
    const sharePath = `/share/space/${encodeURIComponent(slug)}`;
    const url = origin ? `${origin}${sharePath}` : sharePath;
    const cleanDesc = String(desc || "").replace(/\s+/g, " ").trim();
    const snippet = cleanDesc
      ? cleanDesc.length > 140
        ? `${cleanDesc.slice(0, 140).trim()}…`
        : cleanDesc
      : `Check out this space on ${shareBranding.siteName}.`;
    return {
      url,
      title: shareTitle(name, shareBranding),
      text: `${snippet}\n\n— +${slug} on ${shareBranding.siteName}`,
    };
  }, [slug, name, desc, shareBranding]);

  const handleShare = useCallback(async (event) => {
    event?.preventDefault?.();
    event?.stopPropagation?.();
    const payload = buildSharePayload();
    if (!payload) return;
    if (await shareWithSystem(payload)) return;

    setSharePayload(payload);
    setShareOpen(true);
  }, [buildSharePayload]);

  return (
    <>
      <CardShell
        href={`/h/${slug}`}
        className="hover:translate-y-[-1px]"
        square
        edgeMobile
        leading={
          <div className="shrink-0">
            <div className="h-12 w-12 overflow-hidden rounded-2xl ring-2 ring-[var(--border)]">
              {badge ? (
                // eslint-disable-next-line @next/next/no-img-element
                <img
                  src={badge}
                  alt={`${name} badge`}
                  className="h-full w-full object-cover"
                />
              ) : (
                <div
                  aria-hidden
                  className="h-full w-full"
                  style={{
                    background:
                      "linear-gradient(135deg, rgba(124,59,237,.25), rgba(124,59,237,.1))",
                  }}
                />
              )}
            </div>
          </div>
        }
        header={
          <div className="min-w-0">
            <div className="line-clamp-2 break-words text-base font-semibold leading-6 sm:text-lg">
              {name}
            </div>
            <div className="mt-0.5 truncate text-xs text-[var(--muted)] sm:text-sm">+{slug}</div>
          </div>
        }
        actions={
          <div className="ml-auto">
            <button
              type="button"
              onClick={handleShare}
              aria-label={`Share ${name}`}
              className="inline-flex items-center gap-1 rounded-full border border-[var(--border)] px-2.5 py-1 text-xs font-medium text-[var(--muted)] hover:text-[var(--fg-primary)]"
            >
              <Share2 className="h-3.5 w-3.5" />
              Share
            </button>
          </div>
        }
        // We let CardShell handle the default body typography via its bodyClassName
      >
        {/* Description with auto clamp + More/Less (2 lines by default) */}
        {desc ? (
          <CardText
            content={desc}
            variant="markdown"
            clampLines={6}
            expandable
            initiallyExpanded={Boolean(isOpen)} // optional: respects old prop if passed
            // markdownOptions can be passed here if you want more control
          />
        ) : null}
      </CardShell>

      {shareOpen ? (
        <ShareModal
          open={shareOpen}
          onClose={() => {
            setShareOpen(false);
            setSharePayload(null);
          }}
          payload={sharePayload}
        />
      ) : null}
    </>
  );
}

/* local helper */
function toTitle(slug = "") {
  return String(slug)
    .split("-")
    .map((s) => (s ? s[0].toUpperCase() + s.slice(1) : ""))
    .join(" ");
}
